import PropTypes from 'prop-types';
import React from 'react';
import cn from 'classnames';
import { FormattedMessage } from 'react-intl';
import ImmutablePropTypes from 'react-immutable-proptypes';

import { bindAll } from '@bibliocommons/utils-react';
import { PrimaryButton, SecondaryButton, TextButton } from '@bibliocommons/deprecated-base-buttons';
import JacketCover from '@bibliocommons/shared-jacket-cover';
import BibBrief from 'app/components/shared/BibBrief';
import { bibShape, childrenShape } from '@bibliocommons/bc-prop-types';

import ItemFormat from 'app/components/shared/ItemFormat';
import shelfItemShape from 'app/shapes/shelves/shelfItemShape';
import ShelfPrivacyAlert from '../../ShelfPrivacyAlert';

import './BaseUGCEditor.scss';

export default class BaseUGCEditor extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);

    this.state = {
      addAnother: false,
      setToPublic: false
    };
  }

  handleSubmit(e) {
    if (e) {
      e.preventDefault();
    }

    if (!this.props.saveButtonDisabled && !this.isSaving()) {
      this.handleSave();
    }
  }

  handlePrivacyToggle(setToPublic) {
    this.setState({ setToPublic });
  }

  handlePrivacySave() {
    const { addAnother, setToPublic } = this.state;
    const { shelfItem, shelfActions, handleClose, handleReset } = this.props;
    const callback = addAnother ? handleReset : handleClose;

    if (shelfItem && shelfActions && setToPublic) {
      shelfActions.updateShelfItems({
        shelfName: shelfItem.get('shelf'),
        newShelfName: shelfItem.get('shelf'),
        shelfItemIds: [shelfItem.get('id')],
        metadataIds: [shelfItem.get('metadataId')],
        privateItem: false,
        postSuccess: callback
      });
    } else {
      callback();
    }
  }

  handleSave() {
    this.setState({ addAnother: false }, () => {
      this.props.handleSave(this.handlePrivacySave);
    });
  }

  handleSaveAndReset() {
    this.setState({ addAnother: true }, () => {
      this.props.handleSave(this.handlePrivacySave);
    });
  }

  isSaving() {
    const { bib, isSaving, shelfTransactions } = this.props;
    const metadataId = bib.getIn(['briefInfo', 'metadataId']);
    return isSaving || (shelfTransactions && shelfTransactions.getIn([metadataId, 'isUpdatingPrivacy'], false));
  }

  renderPrivacyAlert() {
    const { showPrivacyAlert, renderCustomPrivacyAlert, shelfItem } = this.props;

    if (renderCustomPrivacyAlert) {
      return renderCustomPrivacyAlert();
    }
    if (showPrivacyAlert && shelfItem && shelfItem.get('privateItem')) {
      return <ShelfPrivacyAlert setToPublic={this.state.setToPublic} handleChange={this.handlePrivacyToggle} />;
    }

    return null;
  }

  renderFooter() {
    const { addAnother } = this.state;
    const { handleSave, handleReset, handleClose, saveButtonDisabled } = this.props;

    if (handleSave || handleClose || handleReset) {
      const isSaving = this.isSaving();
      const saveButtonLoading = isSaving && !addAnother;
      const saveAndResetButtonLoading = isSaving && addAnother;

      const saveButton = handleSave && (
        <PrimaryButton
          className="save-button"
          disabled={saveButtonDisabled || saveAndResetButtonLoading}
          isLoading={saveButtonLoading}
          handleClick={this.handleSave}
        >
          <FormattedMessage id="button_save" />
        </PrimaryButton>
      );

      const saveAndResetButton = handleSave && handleReset && (
        <SecondaryButton
          className="save-and-reset-button"
          disabled={saveButtonDisabled || saveButtonLoading}
          isLoading={saveAndResetButtonLoading}
          handleClick={this.handleSaveAndReset}
        >
          <FormattedMessage id="button_save_and_reset" />
        </SecondaryButton>
      );

      const closeButton = handleClose && (
        <TextButton
          className="close-button"
          disabled={saveButtonLoading || saveAndResetButtonLoading}
          handleClick={handleClose}
        >
          <FormattedMessage id="button_cancel" />
        </TextButton>
      );

      return (
        <div className="ugc-editor-footer">
          {saveButton} {saveAndResetButton} {closeButton}
        </div>
      );
    }

    return null;
  }

  renderBibInfo() {
    const { bib } = this.props;

    return (
      <div className="ugc-bib-info">
        <div className="jacket-cover-wrapper">
          <JacketCover src={bib.getIn(['briefInfo', 'jacket'])} format={bib.getIn(['briefInfo', 'format'])} />
        </div>
        <BibBrief bib={bib.get('briefInfo')} titleTag="h3" renderTitleAsLink={false} renderAuthorsAsLink={false}>
          <ItemFormat bib={bib} />
        </BibBrief>
      </div>
    );
  }

  render() {
    const { className, children, renderHeader } = this.props;

    return (
      <div className={cn('cp-base-ugc-editor', className)}>
        <div className="ugc-editor-header">{renderHeader()}</div>
        {this.renderBibInfo()}
        <form onSubmit={this.handleSubmit} className="ugc-editor-form" action="." autoComplete="off">
          <div className="ugc-editor-body">{children}</div>
          {this.renderPrivacyAlert()}
          {this.renderFooter()}
        </form>
      </div>
    );
  }
}

BaseUGCEditor.propTypes = {
  bib: bibShape.isRequired,
  className: PropTypes.string,
  children: childrenShape.isRequired,
  renderHeader: PropTypes.func.isRequired,
  handleSave: PropTypes.func,
  handleClose: PropTypes.func,
  handleReset: PropTypes.func,
  isSaving: PropTypes.bool,
  saveButtonDisabled: PropTypes.bool,
  showPrivacyAlert: PropTypes.bool,
  renderCustomPrivacyAlert: PropTypes.func,
  shelfItem: shelfItemShape,
  shelfTransactions: ImmutablePropTypes.map,
  shelfActions: PropTypes.objectOf(PropTypes.func)
};

BaseUGCEditor.defaultProps = {
  isSaving: false,
  saveButtonDisabled: false,
  showPrivacyAlert: false
};
